import { gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import ReactDOM from 'react-dom'
import styled, { css } from 'styled-components'
import assert from '../../../../common/src/assert'
import { tourTypeToColor } from '../../../../common/src/components/Icon'
import { Link } from '../../../../common/src/components/Link'
import {
  Spacer,
  NoWrapSpan,
  IntroTextSpan,
  BodyTextSpan,
  BodyText,
  IntroText,
} from '../../../../common/src/components/core'
import { Colors } from '../../../../common/src/constants'
import {
  dateTimeFmt,
  timeFmt,
  dateTimeTzFmt,
  dateMDYFmt,
  timeHourFmt,
  dateFmt,
  dateMDNumberFmt,
} from '../../../../common/src/date'
import { lex, reverseMap } from '../../../../common/src/func'
import { showingStatusSort } from '../../../../common/src/sort'
import {
  ConfirmationStatus,
  Contact,
  Showing,
  TourType,
  CalResponseStatus,
  FetchShowingHistoryQuery,
  FetchShowingHistoryQueryVariables,
  FetchShowingsQuery,
} from '../../../../common/src/types.gen'
import { Fragments } from '../../graphql/fragments'
import { PollingQuery } from '../components/PollingQuery'
import { ConfirmationStatusSpan, ShowingRejectButton } from '../components/ShowingStatus'
import { TourTypeWithIcon } from '../components/TourTypeWithIcon'
import { ContactText } from '../components/contact'
import { getThreadPathFor } from '../route'

const fetchShowingHistoryQuery = gql`
  query FetchShowingHistory($showingId: Int!) {
    showing(showingId: $showingId) {
      id
      history {
        ...ShowingContactHistory
      }
    }
  }
  ${Fragments.showingContactHistory}
`

const row1col1 = { gridRow: 1, gridColumn: 1 }
const row1col2 = { gridRow: 1, gridColumn: 2 }

type EventViewMode = 'MINIMAL' | 'FULL' | 'HISTORY'

interface EventItemProps {
  showing: FetchShowingsQuery['task']['showings'][0]
  highlightContactId?: number
  tzName: string
  taskId: number
  noMinimize?: boolean
  deemphasize?: boolean
  filterText?: string
  select?: boolean
  onToggleSelect?: () => void
  showCal: boolean
  includeDate?: boolean
}

interface EventItemState {
  mode: EventViewMode
}

export default class EventItem extends React.PureComponent<EventItemProps, EventItemState> {
  state = {
    mode: 'MINIMAL' as EventViewMode,
  }

  static getDerivedStateFromProps(props: EventItemProps, state: EventItemState) {
    if (props.noMinimize && state.mode === 'MINIMAL') {
      return { mode: 'FULL' }
    }
    return null
  }

  componentDidMount() {
    if (this.props.select) {
      const elem = ReactDOM.findDOMNode(this) as Element
      elem.scrollIntoView()
    }
  }

  render() {
    const { showing, highlightContactId, tzName, filterText, noMinimize, showCal: showCalendar } = this.props
    const { mode } = this.state

    const comparator = lex<FetchShowingsQuery['task']['showings'][0]['showingContacts'][0]>(
      s => s.contact.id !== highlightContactId,
      s => s.isAgent,
      this.computeSortOrder
    )
    const showingContacts = showing.showingContacts.slice().sort(comparator)

    const maxCollapsed = 1
    const numHideable = Math.max(0, showingContacts.length - maxCollapsed)
    const visibleContacts =
      numHideable === 0 || mode === 'FULL' ? showingContacts : showingContacts.slice(0, maxCollapsed)

    const showCal = showCalendar && showing.calEventUrl != null

    const { leftLink, rightLink } = this.renderDetailLinks(showingContacts, noMinimize, mode, numHideable)

    const { select, deemphasize, onToggleSelect, includeDate } = this.props
    const isEmpty = !leftLink && !rightLink && visibleContacts.length === 0
    const time = includeDate ? dateTimeFmt(showing.timeStart, tzName) : timeFmt(showing.timeStart, tzName)

    return (
      <EventItemWrap key={showing.id} select={select} deemphasize={deemphasize} isEmpty={isEmpty}>
        <EventItemHeader>
          <EventItemHeaderLink color="silver" onClick={onToggleSelect}>
            <div style={row1col1}>
              {time}
              {showing.unitType != null && ' ・ ' + showing.unitType}
            </div>
            <EventItemNoWrapSpan style={row1col2}>
              {showCal && <Link href={showing.calEventUrl}>Cal</Link>}
              {showCal && <Spacer width={8} />}
              {showing.tourType != null && this.tourTypeHeader(showing.tourType)}
            </EventItemNoWrapSpan>
          </EventItemHeaderLink>
        </EventItemHeader>
        {mode !== 'HISTORY' &&
          visibleContacts.map(({ contact, isAgent, confirmationStatus, calResponseStatus, timeUpdated }) => (
            <ShowingContactItem
              key={contact.id}
              showing={showing}
              contact={contact}
              isAgent={isAgent}
              confirmationStatus={confirmationStatus}
              calResponseStatus={calResponseStatus}
              isSelected={contact.id === highlightContactId}
              tooltip={'Last changed ' + dateTimeTzFmt(timeUpdated, tzName)}
              filterText={filterText}
            />
          ))}
        {mode === 'HISTORY' && (
          <EventHistory
            showing={showing}
            tzName={tzName}
            highlightContactId={highlightContactId}
            filterText={filterText}
          />
        )}
        {(leftLink || rightLink) && (
          <EventItemFooter>
            {leftLink}
            {rightLink}
            <ShowingRejectButton showing={showing} status={showing.showingStatus} />
          </EventItemFooter>
        )}
      </EventItemWrap>
    )
  }

  /**
   * Given a tourType, return a header element like:
   * Tour Type In Title Case [icon]
   */
  private tourTypeHeader(tourType: TourType) {
    if (!tourType) {
      return null
    }

    return (
      <TourTypeWrap tourType={tourType}>
        <TourTypeWithIcon tourType={tourType} align="right"></TourTypeWithIcon>
      </TourTypeWrap>
    )
  }

  private setModeFull = () => {
    this.setState({ mode: 'FULL' })
  }

  private setModeHist = () => {
    this.setState({ mode: 'HISTORY' })
  }

  private setModeMin = () => {
    this.setState({ mode: 'MINIMAL' })
  }

  private computeSortOrder = (sc: FetchShowingsQuery['task']['showings'][0]['showingContacts'][0]) => {
    const ix = showingStatusSort.indexOf(sc.confirmationStatus)
    assert(ix >= 0, 'Unexpected confirmationStatus ' + sc.confirmationStatus)
    return (ix + 1) * 2 + (sc.isAgent ? 0 : 1)
  }

  private renderDetailLinks(
    showingContacts: FetchShowingsQuery['task']['showings'][0]['showingContacts'],
    noMinimize: boolean | undefined,
    mode: string,
    numHideable: number
  ) {
    let leftLink = null
    let rightLink = null
    if (showingContacts.length > 0) {
      if (noMinimize) {
        if (mode === 'HISTORY') {
          leftLink = (
            <Link color="silver" onClick={this.setModeFull} key="full" style={row1col1}>
              Hide history
            </Link>
          )
        } else {
          leftLink = (
            <Link color="silver" onClick={this.setModeHist} key="hist" style={row1col1}>
              History
            </Link>
          )
        }
      } else {
        if (mode !== 'MINIMAL') {
          leftLink = (
            <Link color="silver" onClick={this.setModeMin} key="min" style={row1col1}>
              ▴ Collapse
            </Link>
          )
        } else if (numHideable > 0) {
          leftLink = (
            <Link color="silver" onClick={this.setModeFull} key="full" style={row1col1}>
              ▾ {numHideable} more
            </Link>
          )
        }
        if (mode !== 'HISTORY') {
          rightLink = (
            <Link color="silver" onClick={this.setModeHist} key="hist" style={row1col2}>
              History
            </Link>
          )
        }
      }
    }
    return { leftLink, rightLink }
  }
}

function ShowingContactItem({
  showing,
  contact,
  isAgent,
  confirmationStatus,
  calResponseStatus,
  isSelected,
  dispTimestamp,
  tooltip,
  filterText,
}: {
  showing: Showing | FetchShowingsQuery['task']['showings'][0]
  contact:
    | Contact
    | {
        __typename: 'Contact'
        id: number
        firstName: string
        lastName: null
        addressEmail: null
        addressPhone: null
        workHours: ''
        language: null
      }
    | FetchShowingsQuery['task']['showings'][0]['showingContacts'][0]['contact']
  isAgent: boolean
  confirmationStatus: ConfirmationStatus // the showing status
  calResponseStatus?: CalResponseStatus | null // the upstream cal event status, for agents
  isSelected: boolean
  dispTimestamp?: string
  tooltip?: string
  filterText?: string
}) {
  let rightSide
  if (calResponseStatus && calResponseStatus !== 'UNKNOWN') {
    // We're displaying a showing confirmation status that comes from a calendar invite.
    // Display only. Do not let the user modify the status--we are not the system of record.
    rightSide = <ConfirmationStatusSpan showingConfStatus={confirmationStatus} calRespStatus={calResponseStatus} />
  } else {
    // We're displaying showing status, which is immutable.
    rightSide = <ConfirmationStatusSpan showingConfStatus={confirmationStatus} />
  }

  return (
    <ShowingContactRow color={isSelected ? 'white' : 'silver'} title={tooltip}>
      <NoWrapSpan>
        {dispTimestamp && <TimestampSpan>{dispTimestamp}</TimestampSpan>}
        <Link
          color={isSelected ? 'white' : 'silver'}
          to={getThreadPathFor({ contactId: contact.id, taskId: showing.leasingTaskId })}
        >
          {isAgent && '▪ '}
          <ContactText contact={contact} filterText={filterText} />
        </Link>
      </NoWrapSpan>
      {rightSide}
    </ShowingContactRow>
  )
}

const TimestampSpan = styled.span`
  width: 80px;
  display: inline-block;
`

const EventItemHeader = styled(IntroTextSpan)`
  white-space: nowrap;
`

const EventItemHeaderLink = styled(Link)`
  display: grid;
  grid-template-columns: auto min-content;
  margin: 6px 0 6px 0;

  &:hover {
    color: ${Colors.snow};
  }

  &:active {
    color: ${Colors.white};
  }
`

const EventItemNoWrapSpan = styled(NoWrapSpan)`
  position: relative;
  top: -1px; /* vertically align tourType text w other header elems */
`

const TourTypeWrap = styled(BodyTextSpan)`
  ${(props: { tourType: TourType }) => css`
    color: ${Colors.midway};

    ${EventItemHeaderLink}:hover & {
      /* make tourType change color with entire header */
      color: ${tourTypeToColor(props.tourType)} !important;
    }

    &:active {
      color: ${Colors.white};
    }
  `};
`

const EventItemFooter = styled(BodyText)`
  display: grid;
  grid-template-columns: auto min-content;
  margin: 6px 0 0 0;
  white-space: nowrap;
`

const EventItemWrap = styled(IntroText)`
  ${(props: { select?: boolean; deemphasize?: boolean; isEmpty: boolean }) => css`
    margin: 16px 0;
    padding: ${props.isEmpty ? '2px 12px 2px 12px' : '2px 12px 8px 12px'};
    border-radius: 4px;
    border: 1px solid ${props.select ? Colors.sky : Colors.charcoal};
    opacity: ${props.deemphasize ? 0.75 : 1};
    transition: all 0.05s linear 0s;
  `};
`

const ShowingContactRow = styled(BodyText)`
  display: flex;
  justify-content: space-between;
  white-space: nowrap;
  transition: all 0.05s linear 0s;

  ${EventItemWrap}:hover & {
    /* afford user focus by dimming all rows not hovered */
    filter: brightness(50%);
  }

  &:hover {
    /* if the user hovers THIS row though, make it shine */
    background: ${Colors.black};
    filter: brightness(120%) !important;
  }

  &:active {
    filter: brightness(140%);
  }
`

interface EventHistoryProps {
  showing: FetchShowingsQuery['task']['showings'][0]
  tzName: string
  highlightContactId?: number
  filterText?: string
}

class EventHistoryImpl extends React.PureComponent<
  EventHistoryProps & QueryResult<FetchShowingHistoryQuery, FetchShowingHistoryQueryVariables>
> {
  render() {
    const { showing, tzName, highlightContactId, filterText, data } = this.props
    if (data == null || !data.showing || !data.showing.history) {
      return <BodyText>Loading history...</BodyText>
    }
    return reverseMap(data.showing.history, (h, i) => {
      const sc = showing.showingContacts.find(s => s.contact.id === h.contactId)
      const strDate = dateMDNumberFmt(h.timeUpdated, tzName)
      const strToday = dateMDYFmt(new Date(), tzName)
      const strTime = timeFmt(h.timeUpdated, tzName)
      const strShortTime = timeHourFmt(h.timeUpdated, tzName)
      const strUser = h.user == null ? 'Bot' : h.user.name
      const strFull = `${dateFmt(h.timeUpdated, tzName)} ${timeFmt(h.timeUpdated, tzName)} – ${strUser}`
      return (
        <ShowingContactItem
          key={i}
          dispTimestamp={strDate === strToday ? strTime : strDate + ' ' + strShortTime}
          showing={showing}
          contact={
            sc == null
              ? {
                  __typename: 'Contact',
                  id: h.contactId,
                  firstName: 'Removed #' + h.contactId,
                  lastName: null,
                  addressEmail: null,
                  addressPhone: null,
                  workHours: '',
                  language: null,
                }
              : sc.contact
          }
          isAgent={sc == null ? false : sc.isAgent}
          confirmationStatus={h.confirmationStatus}
          isSelected={h.contactId === highlightContactId}
          tooltip={strFull}
          filterText={filterText}
        />
      )
    })
  }
}

function EventHistory(props: EventHistoryProps) {
  return (
    <PollingQuery query={fetchShowingHistoryQuery} variables={{ showingId: props.showing.id }}>
      {(res: QueryResult<FetchShowingHistoryQuery, FetchShowingHistoryQueryVariables>) => (
        <EventHistoryImpl {...props} {...res} />
      )}
    </PollingQuery>
  )
}
